import type { UsageMetricRow } from '../aggregate';

/**
 * The shape the usage-metrics dashboard renders from. It mirrors the exported-workbook model the
 * dashboard was originally prototyped against, rebuilt here from the live daily rows so the same
 * panels can run without an export step.
 */

type MonthCounts = {
  newClients: number;
  srs: number;
  notes: number;
  agendas: number;
  recordings: number;
  files: number;
  siesta: number;
  convos: number;
  /**
   * The workbook's "Total actions": the six counted actions including new clients but excluding
   * Siesta. Kept alongside `usage` so the two reconcile against the exported sheet.
   */
  actions: number;
  /**
   * The headline figure. New clients are deliberately excluded - a bulk import of hundreds in one
   * month would swamp every ranking - and Siesta messages are included. Conversations are not
   * added on top of messages, because a conversation is a thread of messages already counted.
   */
  usage: number;
};

type FirmMonth = MonthCounts;

export type Firm = {
  name: string;
  first: string;
  last: string;
  activeMonths: number;
  totals: MonthCounts;
  series: Map<string, FirmMonth>;
  /** Cumulative clients at the end of each month, for the per-100-clients lens. */
  clientsAt: Map<string, number>;
};

export type UsageModel = {
  /** Month keys (YYYY-MM) in chronological order. */
  months: string[];
  monthLabels: Map<string, string>;
  platform: Map<string, MonthCounts>;
  activeFirms: Map<string, number>;
  highUseFirms: Map<string, number>;
  cumulativeClients: Map<string, number>;
  firms: Firm[];
  /** Last complete month - the one the dashboard opens on. */
  reportingMonth: string;
  /** The month still in progress, if the data reaches into one. */
  partialMonth: string | null;
};

export const HIGH_USE_THRESHOLD = 10;

function emptyCounts(): MonthCounts {
  return {
    newClients: 0,
    srs: 0,
    notes: 0,
    agendas: 0,
    recordings: 0,
    files: 0,
    siesta: 0,
    convos: 0,
    actions: 0,
    usage: 0,
  };
}

function addRow(counts: MonthCounts, row: UsageMetricRow) {
  counts.newClients += row.clients;
  counts.srs += row.srs;
  counts.notes += row.notes;
  counts.agendas += row.agendas;
  counts.recordings += row.recordings;
  counts.files += row.files;
  counts.siesta += row.messages;
  counts.convos += row.conversations;
}

function seal(counts: MonthCounts): MonthCounts {
  const counted = counts.srs + counts.notes + counts.agendas + counts.recordings + counts.files;
  counts.actions = counted + counts.newClients;
  counts.usage = counted + counts.siesta;
  return counts;
}

function monthKeyOf(isoDate: string): string {
  return isoDate.slice(0, 7);
}

function monthLabelOf(monthKey: string): string {
  return new Date(`${monthKey}-01T00:00:00.000Z`).toLocaleDateString('en-GB', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Every month between the first and last seen, so a gap renders as a zero rather than vanishing. */
function monthRange(first: string, last: string): string[] {
  const months: string[] = [];
  const [startYear, startMonth] = first.split('-').map(Number);
  const [endYear, endMonth] = last.split('-').map(Number);
  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return months;
}

export function buildUsageModel(rows: readonly UsageMetricRow[], today: Date): UsageModel | null {
  if (rows.length === 0) return null;

  const platform = new Map<string, MonthCounts>();
  const perFirm = new Map<string, Map<string, MonthCounts>>();

  for (const row of rows) {
    const key = monthKeyOf(row.date);

    let platformMonth = platform.get(key);
    if (!platformMonth) {
      platformMonth = emptyCounts();
      platform.set(key, platformMonth);
    }
    addRow(platformMonth, row);

    let firmMonths = perFirm.get(row.company);
    if (!firmMonths) {
      firmMonths = new Map<string, MonthCounts>();
      perFirm.set(row.company, firmMonths);
    }
    let firmMonth = firmMonths.get(key);
    if (!firmMonth) {
      firmMonth = emptyCounts();
      firmMonths.set(key, firmMonth);
    }
    addRow(firmMonth, row);
  }

  const seen = [...platform.keys()].sort();
  const months = monthRange(seen[0], seen[seen.length - 1]);
  const monthLabels = new Map(months.map(month => [month, monthLabelOf(month)]));

  for (const month of months) {
    if (!platform.has(month)) platform.set(month, emptyCounts());
    seal(platform.get(month)!);
  }

  const firms: Firm[] = [];
  for (const [name, firmMonths] of perFirm) {
    const totals = emptyCounts();
    const series = new Map<string, FirmMonth>();
    const clientsAt = new Map<string, number>();
    let activeMonths = 0;
    let first: string | null = null;
    let last: string | null = null;
    let runningClients = 0;

    for (const month of months) {
      const counts = firmMonths.get(month);
      runningClients += counts ? counts.newClients : 0;
      clientsAt.set(month, runningClients);
      if (!counts) continue;

      seal(counts);
      series.set(month, counts);

      totals.newClients += counts.newClients;
      totals.srs += counts.srs;
      totals.notes += counts.notes;
      totals.agendas += counts.agendas;
      totals.recordings += counts.recordings;
      totals.files += counts.files;
      totals.siesta += counts.siesta;
      totals.convos += counts.convos;

      if (counts.usage > 0 || counts.newClients > 0) {
        activeMonths += 1;
        if (first === null) first = month;
        last = month;
      }
    }

    seal(totals);
    firms.push({
      name,
      first: first ?? months[0],
      last: last ?? months[0],
      activeMonths,
      totals,
      series,
      clientsAt,
    });
  }

  firms.sort((a, b) => b.totals.usage - a.totals.usage || a.name.localeCompare(b.name));

  const activeFirms = new Map<string, number>();
  const highUseFirms = new Map<string, number>();
  const cumulativeClients = new Map<string, number>();
  let running = 0;

  for (const month of months) {
    let active = 0;
    let high = 0;
    for (const firm of firms) {
      const counts = firm.series.get(month);
      if (!counts || counts.usage <= 0) continue;
      active += 1;
      if (counts.usage > HIGH_USE_THRESHOLD) high += 1;
    }
    activeFirms.set(month, active);
    highUseFirms.set(month, high);
    running += platform.get(month)!.newClients;
    cumulativeClients.set(month, running);
  }

  const currentMonth = `${today.getUTCFullYear()}-${String(today.getUTCMonth() + 1).padStart(2, '0')}`;
  const partialMonth = months.includes(currentMonth) ? currentMonth : null;
  const complete = months.filter(month => month !== partialMonth);
  const reportingMonth =
    complete.length > 0 ? complete[complete.length - 1] : months[months.length - 1];

  return {
    months,
    monthLabels,
    platform,
    activeFirms,
    highUseFirms,
    cumulativeClients,
    firms,
    reportingMonth,
    partialMonth,
  };
}

export function previousMonth(model: UsageModel, month: string): string | null {
  const index = model.months.indexOf(month);
  return index > 0 ? model.months[index - 1] : null;
}
